<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <SimplePageHeader title="Imports" class="px-4 pr-5 mb-3" />
      <v-row class="px-5">
        <v-col cols="12" sm="6" md="3">
          <ClientsDropDownList
            :id="clientId"
            clearable
            dense
            hide-details
            @clientIdChange="clientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="source"
            :items="sourceOptions"
            label="All Sources"
            clearable
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="statusId"
            :items="statusOptions"
            item-text="name"
            item-value="id"
            label="All Statuses"
            clearable
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <DateRange
            :dateBegin="startedOnOrAfter"
            :dateEnd="startedBefore"
            @dateBeginChange="startedOnOrAfter = $event"
            @dateEndChange="startedBefore = $event"
          />
        </v-col>
      </v-row>
      <v-row class="px-5">
        <v-col cols="12" class="d-flex align-center">
          <v-btn color="primary" outlined @click="handleSearchClick">Search</v-btn>
          <v-btn class="ml-2" outlined @click="handleClearClick">Clear</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="goToUpload">
            <v-icon left>upload_file</v-icon>Upload
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="runList"
          :server-items-length="totalRecords"
          :options.sync="options"
          :footer-props="footerProps"
          class="elevation-2"
        >
          <template v-slot:item.source="{ item }">
            {{ item.vmsIntegrationName || 'Upload' }}
          </template>
          <template v-slot:item.statusName="{ item }">
            <v-chip
              x-small
              label
              :color="statusAttrs(item.statusId).color"
              :dark="statusAttrs(item.statusId).dark"
            >{{ item.statusName }}</v-chip>
          </template>
          <template v-slot:item.rows="{ item }">
            {{ item.importedRows || 0 }} / {{ item.failedRows || 0 }} / {{ item.totalRows || 0 }}
          </template>
          <template v-slot:item.startedAt="{ item }">
            {{ item.startedAt | formatDate }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="viewRun($event, item)"
            >
              <v-icon color="primary">visibility</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ClientsDropDownList } from '@/components/clients'
import { DateRange } from '@/components/shared/datePickers'
import { importRunDetailRoute, importUploadRoute } from '@/lib/constants/routes'
import { IMPORT_RUN_STATUS_OPTIONS, importRunStatusAttrs } from '@/lib/constants/imports'

export default {
  components: {
    BottomSpacer,
    SimplePageHeader,
    ClientsDropDownList,
    DateRange
  },
  data () {
    return {
      statusOptions: IMPORT_RUN_STATUS_OPTIONS,
      options: { page: 1, itemsPerPage: 25 },
      footerProps: { 'items-per-page-options': [10, 25, 50, 100] },
      headers: [
        { text: 'Client', value: 'clientName', sortable: false, width: '20%' },
        { text: 'Source', value: 'source', sortable: false, width: '14%' },
        { text: 'Trigger', value: 'triggerName', sortable: false, width: '10%' },
        { text: 'Status', value: 'statusName', align: 'center', sortable: false, width: '10%' },
        { text: 'Batches', value: 'batchCount', align: 'center', sortable: false, width: '8%' },
        { text: 'Imported / Failed / Total', value: 'rows', align: 'center', sortable: false, width: '16%' },
        { text: 'Started', value: 'startedAt', align: 'center', sortable: false, width: '12%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '8%' }
      ]
    }
  },
  computed: {
    ...mapFields('importRuns', ['runList', 'totalRecords', 'sourceOptions']),
    ...mapFields('importRunFilters', ['clientId', 'source', 'statusId', 'startedOnOrAfter', 'startedBefore'])
  },
  watch: {
    options () {
      this.fetchRuns()
    }
  },
  methods: {
    ...mapActions('importRuns', ['getRunsWithFilters']),
    ...mapActions('importRunFilters', ['clearFilters']),
    statusAttrs (statusId) {
      return importRunStatusAttrs(statusId)
    },
    fetchRuns () {
      return this.getRunsWithFilters({ page: this.options.page, pageSize: this.options.itemsPerPage })
    },
    resetAndFetch () {
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }
      } else {
        this.fetchRuns()
      }
    },
    handleSearchClick () {
      this.resetAndFetch()
    },
    handleClearClick () {
      this.clearFilters()
      this.resetAndFetch()
    },
    goToUpload () {
      this.$router.push({ name: importUploadRoute.name })
    },
    viewRun (event, run) {
      if (event.ctrlKey === true) {
        const route = this.$router.resolve({ name: importRunDetailRoute.name, params: { id: run.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: importRunDetailRoute.name, params: { id: run.id } })
      }
    },
    initialize (returningFromRun) {
      // Keep the filters when the admin comes back from a Run they drilled into, so the breadcrumb
      // returns them to the list exactly as they left it; clear on any fresh entry to the page.
      if (!returningFromRun) {
        this.clearFilters()
      }
      this.fetchRuns()
    }
  },
  beforeRouteEnter (to, from, next) {
    const returningFromRun = from.name === importRunDetailRoute.name
    next(vm => vm.initialize(returningFromRun))
  }
}
</script>
